import { logger } from '../../utils/logger';
import { api } from './base';

export const suppliersApi = {
  list: async (status?: string, approvalStatus?: string) => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (approvalStatus) params.approvalStatus = approvalStatus;
    
    const response = await api.get('/suppliers', { params });
    return response.data.data;
  },

  get: async (id: string) => {
    const response = await api.get(`/suppliers/${id}`);
    return response.data.data;
  },

  create: async (supplier: Record<string, unknown>) => {
    const response = await api.post('/suppliers', supplier);
    return response.data.data;
  },

  update: async (id: string, updates: Record<string, unknown>) => {
    const response = await api.patch(`/suppliers/${id}`, updates);
    return response.data.data;
  },

  delete: async (id: string) => {
    await api.delete(`/suppliers/${id}`);
  },

  // Approved Vendor List (AVL)
  getAVL: async () => {
    const response = await api.get('/suppliers/avl');
    return response.data.data;
  },

  // Purchase Orders
  listPOs: async (supplierId?: string, projectId?: string) => {
    const params: Record<string, string> = {};
    if (supplierId) params.supplierId = supplierId;
    if (projectId) params.projectId = projectId;
    
    const response = await api.get('/purchase-orders', { params });
    return response.data.data;
  },

  getPO: async (poNumber: string) => {
    const response = await api.get(`/purchase-orders/${poNumber}`);
    return response.data.data;
  },

  createPO: async (po: Record<string, unknown>) => {
    const response = await api.post('/purchase-orders', po);
    return response.data.data;
  },

  updatePO: async (poNumber: string, updates: Record<string, unknown>) => {
    const response = await api.patch(`/purchase-orders/${poNumber}`, updates);
    return response.data.data;
  },

  // Receiving Inspections
  getReceivingInspections: async (poNumber: string) => {
    const response = await api.get(`/purchase-orders/${poNumber}/receiving`);
    return response.data.data;
  },

  createReceivingInspection: async (poNumber: string, inspection: Record<string, unknown>) => {
    const response = await api.post(`/purchase-orders/${poNumber}/receiving`, inspection);
    return response.data.data;
  },

  getInspection: async (inspectionId: string) => {
    const response = await api.get(`/receiving-inspections/${inspectionId}`);
    return response.data.data;
  }
};

export const qmsApi = {
  logAccess: async (
    userId: string,
    userName: string,
    documentIdentifier: string,
    documentType: 'certification' | 'design' | 'policy',
    action: 'viewed' | 'downloaded' | 'acknowledged',
    duration?: number
  ) => {
    try {
      const response = await api.post('/qms/access-log', {
        userId,
        userName,
        documentIdentifier,
        documentType,
        action,
        duration
      });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to log access:', error);
      return null;
    }
  },

  getAccessLogs: async (filters?: Record<string, string | number | boolean>) => {
    const response = await api.get('/qms/access-log', { params: filters });
    return response.data.data;
  },

  acknowledge: async (
    userId: string,
    userName: string,
    documentIdentifier: string,
    documentRevision: string,
    mandatory: boolean = true,
    comments?: string
  ) => {
    const response = await api.post('/qms/acknowledge', {
      userId,
      userName,
      documentIdentifier,
      documentRevision,
      mandatory,
      comments
    });
    return response.data.data;
  },

  getAcknowledgments: async (documentId: string, revision?: string) => {
    const response = await api.get(`/qms/acknowledgments/${encodeURIComponent(documentId)}`, {
      params: { revision }
    });
    return response.data.data;
  },

  getMetrics: async () => {
    const response = await api.get('/qms/metrics');
    return response.data.data;
  },

  getDocumentMetrics: async (documentId: string) => {
    const response = await api.get(`/qms/metrics/${encodeURIComponent(documentId)}`);
    return response.data.data;
  },

  getUserHistory: async (userId: string, limit?: number) => {
    const response = await api.get(`/qms/user/${userId}/history`, {
      params: { limit }
    });
    return response.data.data;
  }
};

export const invitationsApi = {
  create: async (email: string, role: string, department?: string) => {
    const response = await api.post('/invitations', { email, role, department });
    return response.data.data;
  },
  
  list: async () => {
    const response = await api.get('/invitations');
    return response.data.data;
  },
  
  resend: async (inviteId: string) => {
    const response = await api.post(`/invitations/${inviteId}/resend`);
    return response.data.data;
  },
  
  revoke: async (inviteId: string) => {
    const response = await api.post(`/invitations/${inviteId}/revoke`);
    return response.data.data;
  },
};

// Component Templates API (ATA Parametric Components)
export const templatesApi = {
  /**
   * List all available component templates (built-in + organization custom)
   */
  list: async () => {
    const response = await api.get('/templates');
    return response.data.data;
  },

  /**
   * Get a specific template by ID
   */
  get: async (templateId: string) => {
    const response = await api.get(`/templates/${templateId}`);
    return response.data.data;
  },

  /**
   * Create a custom template (organization-specific)
   */
  create: async (template: Record<string, unknown>) => {
    const response = await api.post('/templates', template);
    return response.data.data;
  },

  /**
   * Update an existing custom template
   */
  update: async (templateId: string, updates: Record<string, unknown>) => {
    const response = await api.put(`/templates/${templateId}`, updates);
    return response.data.data;
  },

  /**
   * Delete a custom template
   */
  delete: async (templateId: string) => {
    const response = await api.delete(`/templates/${templateId}`);
    return response.data.data;
  },
};
